import { Subject } from 'rxjs';

import { CorrelatedLogger } from '@eluve/logger';

import type { MuteDetector } from '../MuteDetector';
import { MuteDetectionPipeline } from '../mute-detection-pipeline';
import type { MsgFromMain } from '../workers/messages-types';

import type { CaptureProgress } from './pcm-capture-stats';

/**
 * Wall-clock gap (ms) in audio-frame delivery above which we treat the window as
 * a suspend/sleep. Safely above both frame intervals (pcm ~43ms, blob 100ms).
 */
const CAPTURE_GAP_THRESHOLD_MS = 1000;

/**
 * Configuration for creating an AudioSource
 */
export interface AudioSourceConfig {
  mediaStream: MediaStream;
  worker: Worker;
  logger: CorrelatedLogger;
  MuteDetector: typeof MuteDetector;
  deviceId?: string;
  channelCount: number;
  onMute: () => void;
  onUnmute: () => void;
  onBytesReceived: (bytes: number) => void;
  audioPacketSubject: Subject<{ data: Blob; timestamp: number }>;
  /**
   * Invoked when any recording audio track emits `ended` mid-session.
   * Fired at most once per AudioSource lifetime — parent actors use this to
   * dispatch `microphone.disconnected` to their state machine without owning
   * the listener themselves.
   */
  onTrackEnded?: () => void;
}

/**
 * Result of initializing an audio source
 */
export interface AudioSourceInitResult {
  sampleRate: number;
  channels: 1 | 2;
}

/**
 * Abstract base class for audio sources.
 * Handles capturing audio from MediaStream and sending to worker.
 * Implementations handle mode-specific details (PCM vs pre-encoded blobs).
 */
export abstract class AudioSource {
  protected mediaStream: MediaStream;
  protected worker: Worker;
  protected logger: CorrelatedLogger;
  protected MuteDetector: typeof MuteDetector;
  protected deviceId?: string;
  protected channelCount: number;
  protected onMute: () => void;
  protected onUnmute: () => void;
  protected onBytesReceived: (bytes: number) => void;
  protected audioPacketSubject: Subject<{ data: Blob; timestamp: number }>;
  protected onTrackEnded?: () => void;

  protected muteDetectionPipeline: MuteDetectionPipeline | null = null;
  protected audioContext: AudioContext | null = null;
  public waveformAnalyserNode: AnalyserNode | null = null;

  private trackEventCleanup: (() => void) | null = null;

  // Capture-gap detection (device sleep / suspend → silent audio loss).
  // Stamped frames (pcm; worklet stamps seq + captureWallMs) get three distinct
  // metrics: capture gap (real loss), delivery lag (jank/backpressure — audio
  // intact), and transit drops (seq holes). Unstamped frames (blob) fall back
  // to arrival-based detection, which can't tell loss from lateness.
  private lastFrameWallMs = 0;
  private lastCaptureWallMs = 0;
  private lastSeq: number | null = null;
  private captureGapCount = 0;
  private totalCaptureGapMs = 0;
  private peakDeliveryLagMs = 0;
  private droppedFrameCount = 0;

  constructor(config: AudioSourceConfig) {
    this.mediaStream = config.mediaStream;
    this.worker = config.worker;
    this.logger = config.logger;
    this.MuteDetector = config.MuteDetector;
    this.deviceId = config.deviceId;
    this.channelCount = config.channelCount;
    this.onMute = config.onMute;
    this.onUnmute = config.onUnmute;
    this.onBytesReceived = config.onBytesReceived;
    this.audioPacketSubject = config.audioPacketSubject;
    this.onTrackEnded = config.onTrackEnded;
  }

  /**
   * Initialize the audio source and start capturing
   * @returns Sample rate and channel count for worker initialization
   */
  abstract initialize(): Promise<AudioSourceInitResult>;

  /**
   * Stop capturing and clean up resources
   */
  abstract stop(): Promise<void>;

  /**
   * Get the encoding mode for this audio source
   */
  abstract getMode(): 'pcm' | 'blob';

  /**
   * Current AudioContext state, or `null` if no context is owned by this source
   * (e.g. blob/MediaRecorder mode). Surfaced for observability so callers can
   * include it in health-check logs — useful for distinguishing iOS Safari's
   * silent-stall behavior (state stays `running` while the worklet is no longer
   * being clocked) from a normal `suspended` transition.
   */
  getAudioContextState(): AudioContextState | null {
    return this.audioContext?.state ?? null;
  }

  /**
   * Cumulative capture-side progress from the source's own capture pipeline,
   * or `null` when the source has no capture-side counters (blob/MediaRecorder
   * mode — delivery IS the recording there) or none have arrived yet. Lets
   * the audio health check judge data flow from capture truth instead of
   * main-thread message arrival, which browsers throttle in hidden tabs.
   * [ENG-5045]
   */
  getCaptureProgress(): CaptureProgress | null {
    return null;
  }

  /**
   * Set up mute detection using MuteDetectionPipeline
   * @param audioContext Optional AudioContext to reuse; if not provided, pipeline creates its own
   */
  protected setupMuteDetection(audioContext?: AudioContext): void {
    try {
      this.muteDetectionPipeline = new MuteDetectionPipeline({
        mediaStream: this.mediaStream,
        audioContext,
        MuteDetector: this.MuteDetector,
        onMute: this.onMute,
        onUnmute: this.onUnmute,
        logger: this.logger,
      });
      this.muteDetectionPipeline.start();
    } catch (error) {
      this.logger(
        'warn',
        'Failed to initialize mute detection, continuing without it',
        {
          error,
        },
      );
    }
  }

  /**
   * Clean up mute detection resources
   */
  protected async cleanupMuteDetection(): Promise<void> {
    if (this.muteDetectionPipeline) {
      await this.muteDetectionPipeline.stop();
      this.muteDetectionPipeline = null;
    }
  }

  /**
   * Attach `ended` / `mute` / `unmute` listeners to every audio track on the
   * recording MediaStream. `ended` is the required signal that closes the
   * track-lifecycle gap; `mute` / `unmute` are log-only (tagged
   * `source: 'track-event'`) to complement the polled MuteDetector without
   * altering its lifecycle.
   */
  protected setupTrackEventListeners(): void {
    let endedFired = false;

    const tracks = this.mediaStream.getAudioTracks();
    const removers: Array<() => void> = [];

    tracks.forEach((track) => {
      const handleEnded = () => {
        this.logger('warn', 'Audio track ended', {
          deviceId: this.deviceId,
          readyState: track.readyState,
          trackLabel: track.label,
          trackId: track.id,
        });
        if (endedFired) return;
        endedFired = true;
        this.onTrackEnded?.();
      };

      const handleMute = () => {
        this.logger('warn', 'Microphone is muted.', {
          deviceId: this.deviceId,
          source: 'track-event',
          trackId: track.id,
        });
      };

      const handleUnmute = () => {
        this.logger('info', 'Microphone is unmuted.', {
          deviceId: this.deviceId,
          source: 'track-event',
          trackId: track.id,
        });
      };

      track.addEventListener('ended', handleEnded);
      track.addEventListener('mute', handleMute);
      track.addEventListener('unmute', handleUnmute);

      removers.push(() => {
        track.removeEventListener('ended', handleEnded);
        track.removeEventListener('mute', handleMute);
        track.removeEventListener('unmute', handleUnmute);
      });
    });

    this.trackEventCleanup = () => removers.forEach((remove) => remove());
  }

  /**
   * Remove the track-event listeners attached in setupTrackEventListeners.
   */
  protected teardownTrackEventListeners(): void {
    this.trackEventCleanup?.();
    this.trackEventCleanup = null;
  }

  /**
   * Send a message to the worker
   */
  protected postMessage(message: MsgFromMain, transfer?: Transferable[]): void {
    if (message.type === 'pcm' && message.captureWallMs !== undefined) {
      this.checkStampedFrame(message.captureWallMs, message.seq);
    } else if (message.type === 'pcm' || message.type === 'encoded-audio') {
      this.checkArrivalGap();
    }
    if (transfer) {
      this.worker.postMessage(message, transfer);
    } else {
      this.worker.postMessage(message);
    }
  }

  /**
   * Stamped-frame health checks (pcm — the worklet stamps every frame with
   * `captureWallMs` + `seq` at capture time):
   *
   * - Capture gap: consecutive capture stamps jump ≫ frame interval → the
   *   worklet produced nothing for that window (OS suspend/sleep). The audio
   *   is definitively lost — jank can't cause this, because delayed frames
   *   still carry continuous capture stamps.
   * - Delivery lag: arrival wall time minus capture stamp → main-thread jank /
   *   backpressure. Audio intact, just late; tracked as a peak, not logged.
   * - Seq hole: frame produced but never arrived — transit loss.
   */
  private checkStampedFrame(captureWallMs: number, seq?: number): void {
    if (this.lastCaptureWallMs !== 0) {
      const gapMs = captureWallMs - this.lastCaptureWallMs;
      if (gapMs > CAPTURE_GAP_THRESHOLD_MS) {
        this.captureGapCount += 1;
        this.totalCaptureGapMs += gapMs;
        this.logger(
          'warn',
          'Audio capture gap — no frames were produced for this window (device sleep/suspend); the audio is lost',
          {
            gapMs,
            seq,
            captureGapCount: this.captureGapCount,
            totalCaptureGapMs: this.totalCaptureGapMs,
            audioContextState: this.getAudioContextState(),
          },
        );
      }
    }
    this.lastCaptureWallMs = captureWallMs;

    const deliveryLagMs = Date.now() - captureWallMs;
    if (deliveryLagMs > this.peakDeliveryLagMs) {
      this.peakDeliveryLagMs = deliveryLagMs;
    }

    if (seq !== undefined) {
      if (this.lastSeq !== null && seq !== this.lastSeq + 1) {
        const dropped = seq - this.lastSeq - 1;
        this.droppedFrameCount += dropped;
        this.logger('warn', 'Audio frames lost in transit (seq hole)', {
          expectedSeq: this.lastSeq + 1,
          receivedSeq: seq,
          droppedFrameCount: this.droppedFrameCount,
        });
      }
      this.lastSeq = seq;
    }
  }

  /**
   * Arrival-based fallback for unstamped frames (blob mode — MediaRecorder
   * captures internally, so frames can't be stamped at the source). A gap in
   * arrival times is *likely* a suspend, but late delivery after main-thread
   * jank looks identical, so this can overcount loss.
   */
  private checkArrivalGap(): void {
    const now = Date.now();
    if (this.lastFrameWallMs !== 0) {
      const gapMs = now - this.lastFrameWallMs;
      if (gapMs > CAPTURE_GAP_THRESHOLD_MS) {
        this.captureGapCount += 1;
        this.totalCaptureGapMs += gapMs;
        this.logger(
          'warn',
          'Audio capture/delivery gap — likely device sleep/suspend; audio for this window was probably not captured',
          {
            gapMs,
            captureGapCount: this.captureGapCount,
            totalCaptureGapMs: this.totalCaptureGapMs,
            audioContextState: this.getAudioContextState(),
          },
        );
      }
    }
    this.lastFrameWallMs = now;
  }

  /** Cumulative frame-health stats for finalize-time diagnostics. */
  getCaptureGapStats(): {
    captureGapCount: number;
    totalCaptureGapMs: number;
    peakDeliveryLagMs: number;
    droppedFrameCount: number;
  } {
    return {
      captureGapCount: this.captureGapCount,
      totalCaptureGapMs: this.totalCaptureGapMs,
      peakDeliveryLagMs: this.peakDeliveryLagMs,
      droppedFrameCount: this.droppedFrameCount,
    };
  }
}
